import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom"; // Import useLocation and useNavigate
import SortIcon from "../../../assets/svgs/SortIcon";
import "./../tabs.css";
import "../../form-fields/form-ui.css";
import SearchFilterInput from "../../form-fields/search-filter-input";
import UnitTabContent from "./tab-pages/unit-tab-content";
import TenantTabContent from "./tab-pages/tenant-tab-content";
import ServiceRequestTabContent from "./tab-pages/service-request-tab-content";
import TransactionTabContent from "./tab-pages/transaction-tab-content";
import AdditionalDetailTabContent from "./tab-pages/additional-details-tab-content";
import OverviewTabContent from "./tab-pages/overview-tab-content";
import PaymentTabContent from "./tab-pages/payment-tab-content";
import { EntityType, FilterEntityEnum, ServiceRequestEntityTypesEnum } from "@propertelligent/client-api";
import PastLeaseTabContent from "./tab-pages/pastlease-tab-content";
import CurrentTenantOverviewTabContent from "./tab-pages/currentTenanat-overview-tab";
import { usePermissions } from "../../../hooks/usePermissions";
import { useMemo } from "react";
import ListingDetails from "../../listings/listing-details/ListingDetails";

const TAB_PERMISSIONS: Record<string, string> = {
  "Units": "Unit",
  "Tenants": "Tenant",
  "Service Requests": "ServiceRequest",
  "Payments": "Payments",
  "Leases": "Leases",
  "Transactions": "Transaction",
};

interface TabsProps {
  tabs: string[];
  defaultActiveTab: string;
  className?: string;
  entityType?: EntityType;
  isContractor?: boolean;
  contractorPayeeAccountNames?: any;
  onUnitAdded?: () => void;
  onServiceRequestAdded?: () => void;
  tenantUnitId?: number | null;
  unitRefreshTrigger?: number;
  paymentRefreshTrigger?: number;
  unitDataRefreshTrigger?: number;
  onPaymentAction?: () => void;
  expensesDueRaw?: number | string;
  expensesDue?: number | string;
}

export default function Tabs({
  tabs,
  defaultActiveTab,
  entityType, className,
  isContractor = false,
  contractorPayeeAccountNames,
  onUnitAdded,
  onServiceRequestAdded,
  tenantUnitId,
  unitRefreshTrigger,
  paymentRefreshTrigger,
  unitDataRefreshTrigger,
  onPaymentAction,
  expensesDue,
  expensesDueRaw
}: TabsProps) {

  const location = useLocation(); // Get current location
  const navigate = useNavigate(); // Get navigate function
  const { hasAccess } = usePermissions();

  const filteredTabs = useMemo(() => {
    return tabs.filter(tab => {
      let permission = TAB_PERMISSIONS[tab];
      if (tab === "Additional Details") {
        if (entityType === EntityType.Property) permission = "Property";
        else if (entityType === EntityType.Tenant) permission = "Tenant";
        else if (entityType === EntityType.Unit) permission = "Unit";
        else if (entityType === EntityType.Contractor) permission = "Contractor";
      }

      if (!permission) return true;
      return hasAccess(permission, 1);
    });
  }, [tabs, hasAccess, entityType]);

  const [activeTab, setActiveTab] = useState(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("paymentMode")) return "Payments";

    const savedTab = location.state?.activeTab || defaultActiveTab;
    return filteredTabs.includes(savedTab) ? savedTab : (filteredTabs[0] || "");
  });
  const [filterOptions, setFilterOptions] = useState<any>([]);
  const [activeFilterTab, setActiveFilterTab] = useState(filterOptions[0]?.title);
  const [data, setData] = useState([]);
  const [buttonName, setButtonName] = useState("");



  // Update history state when activeTab changes
  useEffect(() => {
    navigate(location.pathname, { state: { activeTab }, replace: true });

    // ⚡ Scroll only the .tabs container to top
    const tabsContainer = document.querySelector(".tabs-body") as HTMLElement;
    if (tabsContainer) {
      tabsContainer.scrollTop = 0;
    }

  }, [activeTab, navigate, location.pathname]);

  const handleFilterTabChange = (tab: string) => setActiveFilterTab(tab);
  const handleTabChange = (tab: string) => {
    if (tab === "Overview") {
      setData([]);
      setFilterOptions([]);
      setButtonName("");
    } else if (tab === "Units") {
       setButtonName("Add Unit");
      setFilterOptions([
        { title: "All", count: 10 },
        { title: "Active", count: 5 },
        { title: "Inactive", count: 5 },
      ]);
      setData([]);
    } else if (tab === "Tenants") {
      setButtonName("Add Tenant");
      setFilterOptions([
        { title: "Current", count: 4 },
        { title: "Prospects", count: 1 },
        { title: "Past", count: 1 },
      ]);
      setData([]);
    } else if (tab === "Service Requests") {
      setButtonName("Add Service Request");
      setFilterOptions([
        { title: "All", count: 6 },
        { title: "New", count: 3 },
        { title: "In Progress", count: 1 },
        { title: "Fixed", count: 1 },
        { title: "Completed", count: 1 },
      ]);
    } else if (tab === "Payments") {
      setButtonName("Add Payment");
      setFilterOptions([
        { title: "Payables", count: 6 },
        { title: "Recievables", count: 3 },
      ]);
    } else if (tab === "Transactions") {
      setButtonName("");
      setFilterOptions([]);
    } else {
      setData([]);
      setFilterOptions([]);
    }
    setActiveTab(tab);
  };


  const renderTabContent = (entityType) => {

    switch (entityType) {
      case EntityType.Property:
        return (
          <>
            {activeTab === "Overview" && <OverviewTabContent result={data} entityTypeId={EntityType.Property} onPaymentAction={onPaymentAction} />}
            {activeTab === "Units" && <UnitTabContent result={data} onUnitAdded={onUnitAdded} unitRefreshTrigger={unitRefreshTrigger} />}
            {activeTab === "Tenants" && <TenantTabContent result={data} />}
            {activeTab === "Service Requests" && <ServiceRequestTabContent entityTypeId={EntityType.Property} onServiceRequestAdded={onServiceRequestAdded} />}
            {activeTab === "Payments" && <PaymentTabContent result={data} entityTypeId={EntityType.Property} refreshTrigger={paymentRefreshTrigger} onRefresh={onPaymentAction} />}
            {activeTab === "Leases" && <PastLeaseTabContent entityType={entityType} />}
            {activeTab === "Transactions" && <TransactionTabContent result={data} entityTypeId={FilterEntityEnum.Property} />}
            {activeTab === "Additional Details" && <AdditionalDetailTabContent result={data} entityTypeId={EntityType.Property} />}
          </>
        );

      case EntityType.Unit:
        return (
          <>
            {activeTab === "Overview" && <OverviewTabContent result={data} entityTypeId={EntityType.Unit} onPaymentAction={onPaymentAction} />}
            {activeTab === "Service Requests" && <ServiceRequestTabContent entityTypeId={EntityType.Unit} />}
            {activeTab === "Payments" && <PaymentTabContent result={data} entityTypeId={EntityType.Unit} refreshTrigger={paymentRefreshTrigger} onRefresh={onPaymentAction} />}
            {activeTab === "Leases" && <PastLeaseTabContent entityType={entityType} />}
            {activeTab === "Transactions" && <TransactionTabContent result={data} entityTypeId={FilterEntityEnum.Unit} />}
            {activeTab === "Listing Details" && <ListingDetails refreshTrigger={unitDataRefreshTrigger} />}

          </>
        );
      case EntityType.Tenant:
        return (
          <>
            {activeTab === "Overview" && <CurrentTenantOverviewTabContent entityTypeId={EntityType.Tenant} onPaymentAction={onPaymentAction} />}
            {activeTab === "Service Requests" && <ServiceRequestTabContent entityTypeId={EntityType.Tenant} tenantUnitId={tenantUnitId} />}
            {activeTab === "Payments" && <PaymentTabContent result={data} entityTypeId={EntityType.Tenant} refreshTrigger={paymentRefreshTrigger} onRefresh={onPaymentAction} expensesDue={expensesDue} expensesDueRaw={expensesDueRaw} />}
            {activeTab === "Leases" && <PastLeaseTabContent entityType={entityType} />}
            {activeTab === "Transactions" && <TransactionTabContent result={data} entityTypeId={FilterEntityEnum.Tenant} />}
            {activeTab === "Additional Details" && <AdditionalDetailTabContent result={data} entityTypeId={EntityType.Tenant} />}

          </>
        );

      case EntityType.Contractor:
        return (
          <>
            {activeTab === "Service Requests" && <ServiceRequestTabContent entityTypeId={EntityType.Contractor} />}
            {activeTab === "Payments" && <PaymentTabContent result={data} entityTypeId={EntityType.Contractor} refreshTrigger={paymentRefreshTrigger} onRefresh={onPaymentAction} expensesDue={expensesDue} expensesDueRaw={expensesDueRaw} />}
            {activeTab === "Transactions" && <TransactionTabContent result={data} isContractor={true} contractorPayeeAccountNames={contractorPayeeAccountNames} entityTypeId={Number(EntityType.Contractor)} />}
            {activeTab === "Additional Details" && <AdditionalDetailTabContent result={data} entityTypeId={EntityType.Contractor} />}
          </>
        );

      default:
        return null;
    }
  };


  return (
    <div className="tabs">
      <div className="tabs-container">
        {filteredTabs.map((tab, index) => (
          <button
            key={index}
            className={`tab ${activeTab === tab ? "tab-active" : ""}`}
            onClick={() => handleTabChange(tab)}>
            {tab}
          </button>
        ))}
      </div>
      {
        <div className="tabs-body">
          {renderTabContent(entityType)}
        </div>
      }
    </div>
  );

}